/**
 * Pending URL Service
 * 
 * This service manages the temporary storage of Sora URLs during the conversion funnel.
 * It uses sessionStorage as primary storage with cookie fallback for cross-tab persistence.
 * 
 * Flow:
 * 1. User pastes URL on landing page -> stored here
 * 2. User signs up/logs in
 * 3. Upscale page retrieves and auto-fills the URL
 * 4. After job starts, the URL is cleared
 */

const PENDING_URL_KEY = 'pending_sora_url';
const PENDING_URL_TIMESTAMP_KEY = 'pending_sora_url_timestamp';
const COOKIE_NAME = 'muzo_pending_sora_url';
const MAX_AGE_HOURS = 24; // URL expires after 24 hours

// Sora URL validation regex
const SORA_URL_REGEX = /^https?:\/\/(?:www\.)?sora\.chatgpt\.com\/p\/[a-z0-9_-]+/i;

const hasStorage = () => typeof window !== 'undefined' && window.sessionStorage;

/**
 * Set a cookie with the given name, value, and max age
 */
const setCookie = (name, value, maxAgeHours) => {
  if (typeof document === 'undefined') return;
  const maxAge = maxAgeHours * 60 * 60; // Convert to seconds
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

/**
 * Get a cookie value by name
 */
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return null;
};

/**
 * Delete a cookie by name
 */
const deleteCookie = (name) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; path=/; max-age=0`;
};

/**
 * Validate if a URL is a valid Sora URL
 */
export const isValidSoraUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return SORA_URL_REGEX.test(url.trim());
};

/**
 * Extract sora_id from URL (e.g., "s_abc123" from "https://sora.chatgpt.com/p/s_abc123")
 */
export const extractSoraIdFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/sora\.chatgpt\.com\/p\/([a-z0-9_-]+)/i);
  return match ? match[1] : null;
};

export const pendingUrlService = {
  /**
   * Store a pending Sora URL
   * @param {string} url - The Sora URL to store
   * @returns {boolean} - Whether the URL was stored successfully
   */
  setPendingUrl(url) {
    if (!url || !isValidSoraUrl(url)) {
      console.warn('[pendingUrlService] Invalid Sora URL, not storing');
      return false;
    }

    const trimmedUrl = url.trim();
    const timestamp = Date.now();

    // Store in sessionStorage (primary)
    if (hasStorage()) {
      try {
        window.sessionStorage.setItem(PENDING_URL_KEY, trimmedUrl);
        window.sessionStorage.setItem(PENDING_URL_TIMESTAMP_KEY, String(timestamp));
      } catch (error) {
        console.warn('[pendingUrlService] Failed to store in sessionStorage', error);
      }
    }

    // Store in cookie (fallback)
    setCookie(COOKIE_NAME, JSON.stringify({ url: trimmedUrl, timestamp }), MAX_AGE_HOURS);

    return true;
  },

  /**
   * Get the pending Sora URL if it exists and hasn't expired
   * @returns {string|null} - The pending URL or null
   */
  getPendingUrl() {
    let url = null;
    let timestamp = null;

    // Try sessionStorage first
    if (hasStorage()) {
      try {
        url = window.sessionStorage.getItem(PENDING_URL_KEY);
        timestamp = window.sessionStorage.getItem(PENDING_URL_TIMESTAMP_KEY);
      } catch (error) {
        console.warn('[pendingUrlService] Failed to read from sessionStorage', error);
      }
    }

    // Fallback to cookie if sessionStorage doesn't have it
    if (!url) {
      const cookieValue = getCookie(COOKIE_NAME);
      if (cookieValue) {
        try {
          const parsed = JSON.parse(cookieValue);
          url = parsed.url;
          timestamp = parsed.timestamp;
        } catch (error) {
          console.warn('[pendingUrlService] Failed to parse cookie', error);
        }
      }
    }

    // Check if URL has expired
    if (url && timestamp) {
      const age = Date.now() - Number(timestamp);
      const maxAge = MAX_AGE_HOURS * 60 * 60 * 1000;
      if (age > maxAge) {
        this.clearPendingUrl();
        return null;
      }
    }

    // Validate the URL
    if (url && !isValidSoraUrl(url)) {
      this.clearPendingUrl();
      return null;
    }

    return url || null;
  },

  /**
   * Check if there's a pending URL
   * @returns {boolean}
   */
  hasPendingUrl() {
    return this.getPendingUrl() !== null;
  },

  /**
   * Clear the pending URL
   */
  clearPendingUrl() {
    // Clear from sessionStorage
    if (hasStorage()) {
      try {
        window.sessionStorage.removeItem(PENDING_URL_KEY);
        window.sessionStorage.removeItem(PENDING_URL_TIMESTAMP_KEY);
      } catch (error) {
        console.warn('[pendingUrlService] Failed to clear sessionStorage', error);
      }
    }

    // Clear from cookie
    deleteCookie(COOKIE_NAME);
  },

  /**
   * Get metadata about the pending URL
   * @returns {{ url: string, timestamp: number, age: number } | null}
   */
  getPendingUrlMeta() {
    const url = this.getPendingUrl();
    if (!url) return null;

    let timestamp = null;

    if (hasStorage()) {
      timestamp = window.sessionStorage.getItem(PENDING_URL_TIMESTAMP_KEY);
    }

    if (!timestamp) {
      const cookieValue = getCookie(COOKIE_NAME);
      if (cookieValue) {
        try {
          const parsed = JSON.parse(cookieValue);
          timestamp = parsed.timestamp;
        } catch (error) {
          // Ignore
        }
      }
    }

    const ts = Number(timestamp) || Date.now();
    return {
      url,
      timestamp: ts,
      age: Date.now() - ts,
      soraId: extractSoraIdFromUrl(url),
    };
  },
};

export default pendingUrlService;
